import React, { useState, useEffect, memo, useCallback } from 'react';
import { format } from 'date-fns';

const VicNotifTable = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hoveredRow, setHoveredRow] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });

  const fetchVicData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch('/api/vic/getMissingVics');

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      const result = await response.json();
      setData(Array.isArray(result) ? result : []);
    } catch (err) {
      console.error('Erreur lors du chargement des données VIC:', err);
      setError(err.message);
      setData([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchVicData();
  }, [fetchVicData]);

  const calculateTooltipPosition = useCallback((mouseX, mouseY) => {
    const tooltipWidth = 250;
    const tooltipHeight = 130;
    const offset = 10;
    
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    
    let x = mouseX + offset;
    let y = mouseY + offset;
    
    if (x + tooltipWidth > viewportWidth) {
      x = mouseX - tooltipWidth - offset;
    }
    
    if (y + tooltipHeight > viewportHeight) {
      y = mouseY - tooltipHeight - offset;
    }
    
    if (x < 0) {
      x = offset;
    }
    
    if (y < 0) {
      y = offset;
    }
    
    return { x, y };
  }, []);

  const handleMouseMove = useCallback((e) => {
    const position = calculateTooltipPosition(e.clientX, e.clientY);
    setTooltipPosition(position);
  }, [calculateTooltipPosition]);

  const renderVicItem = useCallback((vic, index) => (
    <div
      key={vic.id || index}
      className={`vic-item ${hoveredRow === index ? 'hovered' : ''}`}
      onMouseEnter={() => setHoveredRow(index)}
      onMouseLeave={() => setHoveredRow(null)}
      onMouseMove={handleMouseMove}
    >
      <span className="vic-dossier" style={!vic.ndossier ? { color: '#dc3545', fontStyle: 'italic' } : {}}>
        {vic.ndossier || 'Indisponible'}
      </span>
      <span className="vic-separator"> - </span>
      <span className="vic-lot" style={!vic.lot ? { color: '#dc3545', fontStyle: 'italic' } : {}}>
        {vic.lot || 'Indisponible'}
      </span>
      <span className="vic-separator"> - </span>
      <span className="vic-company" style={!vic.company ? { color: '#dc3545', fontStyle: 'italic' } : {}}>
        {vic.company || 'Indisponible'}
      </span>
    </div>
  ), [hoveredRow, handleMouseMove]);

  if (loading) {
    return (
      <div className="vic-notif-table loading">
        <div className="loading-spinner">Chargement...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="vic-notif-table error">
        <div className="error-message">
          Erreur: {error}
        </div>
        <button
          onClick={fetchVicData}
          className="retry-button"
        >
          Réessayer
        </button>
      </div>
    );
  }

  if (!data.length) {
    return (
      <div className="vic-notif-table empty">
        <div className="empty-message">Aucune VIC manquante trouvée.</div>
      </div>
    );
  }

  return (
    <div className="vic-notif-table">
      <div className="vic-notif-header">
        <span>VIC manquante{data.length > 1 && 's'} ({data.length})</span>
      </div>
      <div className="vic-notif-list">
        {data.map(renderVicItem)}
      </div>
      {hoveredRow !== null && (
        <div
          className="vic-notif-tooltip"
          style={{ 
            left: tooltipPosition.x, 
            top: tooltipPosition.y,
            position: 'fixed'
          }}
        >
          <p><b>Dossier:</b> <span style={!data[hoveredRow].ndossier ? { color: '#dc3545', fontStyle: 'italic' } : {}}>{data[hoveredRow].ndossier || 'Indisponible'}</span></p>
          <p><b>Lot:</b> <span style={!data[hoveredRow].lot ? { color: '#dc3545', fontStyle: 'italic' } : {}}>{data[hoveredRow].lot || 'Indisponible'}</span></p>
          <p><b>Entreprise:</b> <span style={!data[hoveredRow].company ? { color: '#dc3545', fontStyle: 'italic' } : {}}>{data[hoveredRow].company || 'Indisponible'}</span></p>
          <p><b>Date d'intervention:</b> <span style={!data[hoveredRow].intervention_date ? { color: '#dc3545', fontStyle: 'italic' } : {}}>{data[hoveredRow]?.intervention_date ? format(data[hoveredRow]?.intervention_date, 'dd/MM/yyyy') : 'Indisponible'}</span></p>
          <p><b>Représentant:</b> <span style={!data[hoveredRow].representative ? { color: '#dc3545', fontStyle: 'italic' } : {}}>{data[hoveredRow].representative || 'Indisponible'}</span></p>
          <p><b>Tel:</b> <span style={!data[hoveredRow].tel ? { color: '#dc3545', fontStyle: 'italic' } : {}}>{data[hoveredRow].tel || 'Indisponible'}</span></p>
        </div>
      )}
    </div>
  );
};

export default memo(VicNotifTable);